import React, { useRef, useState, useEffect, useMemo } from 'react';
import { ShaderMount } from '@paper-design/shaders-react';
import {
  liquidMetalFragmentShader,
  getShaderColorFromString,
  ShaderFitOptions,
} from '@paper-design/shaders';

const BASE_SPEED = 0.25;
const MAX_SPEED = 3.5;
const DECAY = 0.95;

const PROCESSED_IMAGE = '/splav-logo-processed.png';

export const MetallicLogo = ({ className = "", style = {}, onReady, bgColor = '#000000' }) => {
  const [speed, setSpeed] = useState(BASE_SPEED);
  const containerRef = useRef(null);
  const lastPos = useRef({ x: 0, y: 0 });
  const speedRef = useRef(BASE_SPEED);
  const frameRef = useRef(null);
  const calledBack = useRef(false);

  // Detect when canvas is created inside ShaderMount
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !onReady) return;

    const notify = () => {
      if (!calledBack.current) {
        calledBack.current = true;
        onReady();
      }
    };

    // Already there?
    if (el.querySelector('canvas')) {
      notify();
      return;
    }

    const observer = new MutationObserver(() => {
      if (el.querySelector('canvas')) {
        observer.disconnect();
        notify();
      }
    });
    observer.observe(el, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [onReady]);

  useEffect(() => {
    const onMouseMove = (e) => {
      const dx = e.clientX - lastPos.current.x;
      const dy = e.clientY - lastPos.current.y;
      const velocity = Math.sqrt(dx * dx + dy * dy);
      lastPos.current = { x: e.clientX, y: e.clientY };
      const boost = Math.min(velocity * 0.06, MAX_SPEED - BASE_SPEED);
      speedRef.current = Math.max(speedRef.current, BASE_SPEED + boost);
    };
    window.addEventListener('mousemove', onMouseMove);
    return () => window.removeEventListener('mousemove', onMouseMove);
  }, []);

  useEffect(() => {
    const animate = () => {
      speedRef.current = BASE_SPEED + (speedRef.current - BASE_SPEED) * DECAY;
      if (speedRef.current - BASE_SPEED < 0.01) speedRef.current = BASE_SPEED;
      setSpeed(speedRef.current);
      frameRef.current = requestAnimationFrame(animate);
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameRef.current);
  }, []);

  const [dims, setDims] = useState({ w: 450, h: 212 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width > 0 && height > 0) setDims({ w: Math.round(width), h: Math.round(height) });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const uniforms = useMemo(() => ({
    u_colorBack: getShaderColorFromString(bgColor),
    u_colorTint: getShaderColorFromString('#ffffff'),
    u_image: PROCESSED_IMAGE,
    u_contour: 0.8,
    u_distortion: 0.56,
    u_softness: 0.69,
    u_repetition: 6.23,
    u_shiftRed: 0.3,
    u_shiftBlue: 0.3,
    u_angle: 110,
    u_isImage: true,
    u_shape: 0,
    u_fit: ShaderFitOptions['contain'],
    u_scale: 0.8,
    u_rotation: 0,
    u_offsetX: 0,
    u_offsetY: 0,
    u_originX: 0.5,
    u_originY: 0.5,
    u_worldWidth: 0,
    u_worldHeight: 0,
  }), [bgColor]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        ...style,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 0,
      }}
      data-testid="metallic-logo"
    >
      <ShaderMount
        fragmentShader={liquidMetalFragmentShader}
        uniforms={uniforms}
        speed={speed}
        mipmaps={['u_image']}
        width={dims.w * 2}
        height={dims.h * 2}
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  );
};

export default MetallicLogo;
